import { createAction, props } from '@ngrx/store';
import {
  ActionDetailsDto,
  Comment,
  Procedure,
  ProcedureDetailsDto,
  ProcedureItemBase,
  ProcedureItemsResponse,
  ProcedureItemUpdateResponse,
  ProcedureStatus,
  ProcedureStatusDisplayKey,
} from 'projects/shared/src/lib/core/interfaces';
import { baseProps } from 'projects/shared/src/lib/core/store/actions-base';

export const loadProcedures = createAction(
  '[@diso/policies-and-procedures] loadProcedures',
  baseProps(),
);

export const loadProceduresSuccess = createAction(
  '[@diso/policies-and-procedures] loadProceduresSuccess',
  props<{ procedures: Procedure[] }>(),
);

export const loadProceduresFailure = createAction(
  '[@diso/policies-and-procedures] loadProceduresFailure',
);

// Procedure Items Actions
export const loadProcedureHeader = createAction(
  '[@diso/policies-and-procedures] loadProcedureHeader',
  baseProps<{ procedureId: string }>(),
);

export const loadProcedureHeaderSuccess = createAction(
  '[@diso/policies-and-procedures] loadProcedureHeaderSuccess',
  props<{ procedure: Procedure }>(),
);

export const loadProcedureHeaderFailure = createAction(
  '[@diso/policies-and-procedures] loadProcedureHeaderFailure',
);

export const loadProcedureItems = createAction(
  '[@diso/policies-and-procedures] loadProcedureItems',
  baseProps<{ procedureId: string }>(),
);

export const loadProcedureItemsSuccess = createAction(
  '[@diso/policies-and-procedures] loadProcedureItemsSuccess',
  props<{ procedureId: string; items: ProcedureItemsResponse }>(),
);

export const loadProcedureItemsFailure = createAction(
  '[@diso/policies-and-procedures] loadProcedureItemsFailure',
);

export const startProcedureItemStatusChange = createAction(
  '[@diso/policies-and-procedures] startProcedureItemStatusChange',
  props<{ itemId: string }>(),
);

export const approveProcedureItemStatus = createAction(
  '[@diso/policies-and-procedures] approveProcedureItemStatus',
  baseProps<{
    procedureId: string;
    itemId: string;
  }>(),
);

export const approveProcedureItemStatusStatusSuccess = createAction(
  '[@diso/policies-and-procedures] approveProcedureItemStatusStatusSuccess',
  props<{
    procedureId: string;
    itemId: string;
    response: ProcedureItemUpdateResponse;
  }>(),
);

export const approveProcedureItemStatusStatusFailure = createAction(
  '[@diso/policies-and-procedures] approveProcedureItemStatusStatusFailure',
  props<{ itemId: string }>(),
);

export const unApproveProcedureItemStatus = createAction(
  '[@diso/policies-and-procedures] unApproveProcedureItemStatus',
  baseProps<{
    procedureId: string;
    itemId: string;
    correlationId: string;
    targetStatus: 'Pending' | 'Skipped';
    rowVersion?: string;
  }>(),
);

export const unApproveProcedureItemStatusStatusSuccess = createAction(
  '[@diso/policies-and-procedures] unApproveProcedureItemStatusStatusSuccess',
  props<{
    procedureId: string;
    itemId: string;
    response: ProcedureItemUpdateResponse;
  }>(),
);

export const unApproveProcedureItemStatusStatusFailure = createAction(
  '[@diso/policies-and-procedures] unApproveProcedureItemStatusStatusFailure',
  props<{ itemId: string }>(),
);

export const refreshProcedureItems = createAction(
  '[@diso/policies-and-procedures] refreshProcedureItems',
  props<{ procedureId: string }>(),
);

export const fetchProcedureStatuses = createAction(
  '[@diso/policies-and-procedures] fetchProcedureStatuses',
);

export const fetchProcedureStatusesSuccess = createAction(
  '[@diso/policies-and-procedures] fetchProcedureStatusesSuccess',
  props<{ statuses: ProcedureStatus[] }>(),
);

export const fetchProcedureItemStatuses = createAction(
  '[@diso/policies-and-procedures] fetchProcedureItemStatuses',
);

export const fetchProcedureItemStatusesSuccess = createAction(
  '[@diso/policies-and-procedures] fetchProcedureItemStatusesSuccess',
  props<{ statuses: ProcedureStatus[] }>(),
);

export const changeProcedureStatus = createAction(
  '[@diso/policies-and-procedures] changeProcedureStatus',
  baseProps<{
    procedureId: string;
  }>(),
);
export const activateProcedure = createAction(
  '[@diso/policies-and-procedures] activateProcedure',
  baseProps<{
    procedureId: string;
    correlationId: string;
    rowVersion?: string;
  }>(),
);

export const activateProcedureSuccess = createAction(
  '[@diso/policies-and-procedures] activateProcedureSuccess',
  props<{
    procedureId: string;
    newStatusCode: ProcedureStatusDisplayKey;
    activatedOn: string;
  }>(),
);

export const activateProcedureFailure = createAction(
  '[@diso/policies-and-procedures] activateProcedureFailure',
  props<{ procedureId: string }>(),
);

export const deActivateProcedure = createAction(
  '[@diso/policies-and-procedures] deActivateProcedure',
  baseProps<{
    procedureId: string;
    correlationId: string;
    rowVersion?: string;
  }>(),
);

export const deActivateProcedureSuccess = createAction(
  '[@diso/policies-and-procedures] deActivateProcedureSuccess',
  props<{
    procedureId: string;
    newStatusCode: ProcedureStatusDisplayKey;
    deactivatedOn: string;
  }>(),
);

export const deActivateProcedureFailure = createAction(
  '[@diso/policies-and-procedures] deActivateProcedureFailure',
  props<{ procedureId: string }>(),
);

export const toggleProcedureSidebar = createAction(
  '[@diso/policies-and-procedures] toggleProcedureSidebar',
  props<{ show: boolean; procedureId?: string }>(),
);

export const fetchProcedureDetails = createAction(
  '[@diso/policies-and-procedures] fetchProcedureDetails',
  baseProps<{ procedureId: string }>(),
);

export const fetchProcedureDetailsSuccess = createAction(
  '[@diso/policies-and-procedures] fetchProcedureDetailsSuccess',
  props<{ procedureDetails: ProcedureDetailsDto }>(),
);

export const fetchProcedureDetailsFailure = createAction(
  '[@diso/policies-and-procedures] fetchProcedureDetailsFailure',
);

export const hideProcedureSidebar = createAction(
  '[@diso/policies-and-procedures] hideProcedureSidebar',
);

export const addProcedureComment = createAction(
  '[@diso/policies-and-procedures] addProcedureComment',
  props<{ procedureId: string; commentText: string }>(),
);

export const addProcedureCommentSuccess = createAction(
  '[@diso/policies-and-procedures] addProcedureCommentSuccess',
  props<{ comment: Comment }>(),
);

export const addProcedureCommentFailure = createAction(
  '[@diso/policies-and-procedures] addProcedureCommentFailure',
);

export const setProcedureCommentPending = createAction(
  '[@diso/policies-and-procedures] setProcedureCommentPending',
  props<{ pending: boolean }>(),
);

export const setProcedureCommentFailed = createAction(
  '[@diso/policies-and-procedures] setProcedureCommentFailed',
  props<{ failed: boolean }>(),
);

// Action Item Sidebar Actions
export const toggleActionItemSidebar = createAction(
  '[@diso/policies-and-procedures] toggleActionItemSidebar',
  props<{ show: boolean; activeProcedureItemId?: string }>(),
);

export const fetchActionDetails = createAction(
  '[@diso/policies-and-procedures] fetchActionDetails',
  baseProps<{ activeProcedureItemId: string }>(),
);

export const fetchActionDetailsSuccess = createAction(
  '[@diso/policies-and-procedures] fetchActionDetailsSuccess',
  props<{ actionDetails: ActionDetailsDto }>(),
);

export const fetchActionDetailsFailure = createAction(
  '[@diso/policies-and-procedures] fetchActionDetailsFailure',
);

export const hideActionItemSidebar = createAction(
  '[@diso/policies-and-procedures] hideActionItemSidebar',
);

export const addActionItemComment = createAction(
  '[@diso/policies-and-procedures] addActionItemComment',
  props<{ activeProcedureItemId: string; commentText: string }>(),
);

export const addActionItemCommentSuccess = createAction(
  '[@diso/policies-and-procedures] addActionItemCommentSuccess',
  props<{ comment: Comment }>(),
);

export const addActionItemCommentFailure = createAction(
  '[@diso/policies-and-procedures] addActionItemCommentFailure',
);

export const setActionItemCommentPending = createAction(
  '[@diso/policies-and-procedures] setActionItemCommentPending',
  props<{ pending: boolean }>(),
);

export const setActionItemCommentFailed = createAction(
  '[@diso/policies-and-procedures] setActionItemCommentFailed',
  props<{ failed: boolean }>(),
);

export const selectProcedureItem = createAction(
  '[@diso/policies-and-procedures] selectProcedureItem',
  props<{ item: ProcedureItemBase }>(),
);

export const approvePolicyActions = createAction(
  '[@diso/policies-and-procedures] approvePolicyActions',
  props<{ procedureId: string; policyId: string }>(),
);

export const approvePolicyActionsSuccess = createAction(
  '[@diso/policies-and-procedures] approvePolicyActionsSuccess',
  props<{
    procedureId: string;
    policyId: string;
    allItemsApproved: boolean;
    itemsApprovedCount: number;
    totalCount: number;
  }>(),
);

export const approvePolicyActionsFailure = createAction(
  '[@diso/policies-and-procedures] approvePolicyActionsFailure',
);

export const approveAllActions = createAction(
  '[@diso/policies-and-procedures] approveAllActions',
  props<{ procedureId: string }>(),
);

export const approveAllActionsSuccess = createAction(
  '[@diso/policies-and-procedures] approveAllActionsSuccess',
  props<{
    procedureId: string;
    allItemsApproved: boolean;
    itemsApprovedCount: number;
    totalCount: number;
  }>(),
);

export const approveAllActionsFailure = createAction(
  '[@diso/policies-and-procedures] approveAllActionsFailure',
);

export const approveAllSubProcedures = createAction(
  '[@diso/policies-and-procedures] approveAllSubProcedures',
  props<{ procedureId: string }>(),
);

export const approveAllSubProceduresSuccess = createAction(
  '[@diso/policies-and-procedures] approveAllSubProceduresSuccess',
  props<{
    procedureId: string;
    allItemsApproved: boolean;
    itemsApprovedCount: number;
    totalCount: number;
  }>(),
);

export const approveAllSubProceduresFailure = createAction(
  '[@diso/policies-and-procedures] approveAllSubProceduresFailure',
);
